//LIBRARIES
import React, { Fragment } from 'react';
import styled from 'styled-components';
import isEqual from 'lodash/isEqual';

//COMPONENTS
import ToggleSwitch from 'components/ui/ToggleSwitch';
import Spacer from 'components/ui/Spacer';
import Form from 'components/ui/Form';
import { RenderIfToggleOn } from 'components/shared/ToggleRenderer';

//HELPERS
import { getTranslatedString } from 'helpers/i18n';

const Title = styled.p`
  font-size: 18px;
  font-weight: bold;
  margin-top: 40px;
  margin-bottom: 12px;
`;

const renderAgentPreferences = (values, setFieldValue) => (
  <Fragment>
    <Form.Item>
      <ToggleSwitch
        name="maintenance_notifications"
        isChecked={values['preference_attributes']['maintenance_notifications']}
        onChange={(value) =>
          setFieldValue('preference_attributes.maintenance_notifications', value)
        }
      />
    </Form.Item>
    <Form.Item>
      <ToggleSwitch
        name="can_manually_assign"
        isChecked={values['can_manually_assign']}
        onChange={(value) => setFieldValue('can_manually_assign', value)}
      />
    </Form.Item>
    <Form.Item>
      <ToggleSwitch
        name="notice_template_manager"
        isChecked={values['notice_template_manager']}
        onChange={(value) => setFieldValue('notice_template_manager', value)}
      />
    </Form.Item>
    <Form.Item>
      <ToggleSwitch
        name="can_only_send_by_notice_template"
        isChecked={values['can_only_send_by_notice_template']}
        onChange={(value) => setFieldValue('can_only_send_by_notice_template', value)}
      />
    </Form.Item>
    <Form.Item>
      <ToggleSwitch
        name="can_add_amenities"
        isChecked={values['can_add_amenities']}
        onChange={(value) => setFieldValue('can_add_amenities', value)}
      />
    </Form.Item>
    <Form.Item>
      <ToggleSwitch
        name="lease_signing_authority"
        isChecked={values['lease_signing_authority']}
        onChange={(value) => setFieldValue('lease_signing_authority', value)}
      />
    </Form.Item>
    <Form.Item>
      <ToggleSwitch
        name="lease_creation_authority"
        isChecked={values['lease_creation_authority']}
        onChange={(value) => setFieldValue('lease_creation_authority', value)}
      />
    </Form.Item>
    <Form.Item>
      <ToggleSwitch
        name="can_approve_leases"
        isChecked={values['can_approve_leases']}
        onChange={(value) => setFieldValue('can_approve_leases', value)}
      />
    </Form.Item>
    <Form.Item>
      <ToggleSwitch
        name="can_view_credit_check"
        isChecked={values['can_view_credit_check']}
        onChange={(value) => setFieldValue('can_view_credit_check', value)}
      />
    </Form.Item>
    <Form.Item>
      <ToggleSwitch
        name="can_approve_application"
        isChecked={values['can_approve_application']}
        onChange={(value) => setFieldValue('can_approve_application', value)}
      />
    </Form.Item>
    <Form.Item>
      <ToggleSwitch
        name="call_center"
        isChecked={values['call_center']}
        onChange={(value) => setFieldValue('call_center', value)}
      />
    </Form.Item>
    <Form.Item>
      <ToggleSwitch
        name="show_availability"
        isChecked={values['show_availability']}
        onChange={(value) => setFieldValue('show_availability', value)}
      />
    </Form.Item>
    <Form.Item>
      <ToggleSwitch
        name="allow_manual_agent_bookings"
        isChecked={values['allow_manual_agent_bookings']}
        onChange={(value) => setFieldValue('allow_manual_agent_bookings', value)}
      />
    </Form.Item>
    <Form.Item>
      <ToggleSwitch
        name="escalate_maintenance_tickets"
        isChecked={values['escalate_maintenance_tickets']}
        onChange={(value) => setFieldValue('escalate_maintenance_tickets', value)}
      />
    </Form.Item>
    <Form.Item>
      <ToggleSwitch
        name="receive_not_signed_up_leads_notification"
        isChecked={values['receive_not_signed_up_leads_notification']}
        onChange={(value) => setFieldValue('receive_not_signed_up_leads_notification', value)}
      />
    </Form.Item>
    <Form.Item>
      <ToggleSwitch
        name="is_manager"
        isChecked={values['is_manager']}
        onChange={(value) => setFieldValue('is_manager', value)}
      />
    </Form.Item>
    <Form.Item>
      <ToggleSwitch
        name="enable_two_factor_auth"
        isChecked={values['enable_two_factor_auth']}
        onChange={(value) => setFieldValue('enable_two_factor_auth', value)}
      />
    </Form.Item>
    <RenderIfToggleOn toggle="purchase_order">
      <Form.Item>
        <ToggleSwitch
          name="can_approve_purchase_order"
          isChecked={values['can_approve_purchase_order']}
          onChange={(value) => setFieldValue('can_approve_purchase_order', value)}
        />
      </Form.Item>
    </RenderIfToggleOn>

    <RenderIfToggleOn toggle="ui.maintenance.new_tickets_ui">
      <RenderIfToggleOn toggle="maintenance.scheduling">
        <Form.Item>
          <ToggleSwitch
            name="can_assign_and_schedule_tickets"
            isChecked={values['can_assign_and_schedule_tickets']}
            onChange={(value) => setFieldValue('can_assign_and_schedule_tickets', value)}
          />
        </Form.Item>
      </RenderIfToggleOn>
      <Form.Item>
        <ToggleSwitch
          name="can_log_travel_and_work_times"
          isChecked={values['can_log_travel_and_work_times']}
          onChange={(value) => setFieldValue('can_log_travel_and_work_times', value)}
        />
      </Form.Item>
      <Form.Item>
        <ToggleSwitch
          name="can_change_ticket_status"
          isChecked={values['can_change_ticket_status']}
          onChange={(value) => setFieldValue('can_change_ticket_status', value)}
        />
      </Form.Item>
    </RenderIfToggleOn>
  </Fragment>
);

const renderMaintenancePreferences = (values, setFieldValue) => (
  <Fragment>
    <Form.Item>
      <ToggleSwitch
        name="maintenance_notifications"
        isChecked={values['preference_attributes']['maintenance_notifications']}
        onChange={(value) =>
          setFieldValue('preference_attributes.maintenance_notifications', value)
        }
      />
    </Form.Item>
    <Form.Item>
      <ToggleSwitch
        name="read_only"
        isChecked={values['read_only']}
        onChange={(value) => setFieldValue('read_only', value)}
      />
    </Form.Item>
    <Form.Item>
      <ToggleSwitch
        name="hide_branding"
        isChecked={values['hide_branding']}
        onChange={(value) => setFieldValue('hide_branding', value)}
      />
    </Form.Item>
    <Form.Item>
      <ToggleSwitch
        name="enable_two_factor_auth"
        isChecked={values['enable_two_factor_auth']}
        onChange={(value) => setFieldValue('enable_two_factor_auth', value)}
      />
    </Form.Item>
    <Form.Item>
      <ToggleSwitch
        name="claim_tickets"
        isChecked={values['claim_tickets']}
        onChange={(value) => setFieldValue('claim_tickets', value)}
      />
    </Form.Item>
    <Form.Item>
      <ToggleSwitch
        name="assign_tickets"
        isChecked={values['assign_tickets']}
        onChange={(value) => setFieldValue('assign_tickets', value)}
      />
    </Form.Item>
    <Form.Item>
      <ToggleSwitch
        name="view_all_tickets"
        isChecked={values['view_all_tickets']}
        onChange={(value) => setFieldValue('view_all_tickets', value)}
      />
    </Form.Item>
    <Form.Item>
      <ToggleSwitch
        name="view_non_maintenance_tickets"
        isChecked={values['view_non_maintenance_tickets']}
        onChange={(value) => setFieldValue('view_non_maintenance_tickets', value)}
      />
    </Form.Item>
  </Fragment>
);

const PreferenceFields = ({ values, setFieldValue }) => {
  const renderPreferencesBody = isEqual(values.user_type, 'Agent')
    ? renderAgentPreferences(values, setFieldValue)
    : renderMaintenancePreferences(values, setFieldValue);

  return (
    <Fragment>
      <Spacer mb={1}>
        <Title>{getTranslatedString('landlordPortal.users.form.otherOptionsTitle')}</Title>
        {renderPreferencesBody}
      </Spacer>
    </Fragment>
  );
};

export default PreferenceFields;
